/**
 * Global polyfills for Node.js modules in browser environment
 * This must be loaded before any other modules that might use Node.js APIs
 */

// Global storage for AsyncLocalStorage instances
const globalAsyncStorage = new Map();
let globalStoreCounter = 0;
let defaultStore = null;

// AsyncLocalStorage polyfill with more aggressive compatibility
class AsyncLocalStoragePolyfill {
  constructor() {
    this.id = `als_${++globalStoreCounter}`;
    this.currentStore = null;
  }
  
  run(store, callback, ...args) {
    const previousStore = this.currentStore;
    this.currentStore = store;
    globalAsyncStorage.set(this.id, store);
    defaultStore = store; // Set as default for any orphaned calls
    
    try {
      if (typeof callback === 'function') {
        return callback(...args);
      }
      return callback;
    } finally {
      this.currentStore = previousStore;
      if (previousStore !== null) {
        globalAsyncStorage.set(this.id, previousStore);
        defaultStore = previousStore;
      } else {
        globalAsyncStorage.delete(this.id);
        defaultStore = null;
      }
    }
  }
  
  getStore() {
    // Try multiple fallback strategies
    return this.currentStore || 
           globalAsyncStorage.get(this.id) || 
           defaultStore || 
           null;
  }
  
  enterWith(store) {
    this.currentStore = store;
    globalAsyncStorage.set(this.id, store);
    defaultStore = store;
  }
  
  exit(callback, ...args) {
    const previousStore = this.currentStore;
    this.currentStore = null;
    globalAsyncStorage.delete(this.id);
    
    try {
      if (typeof callback === 'function') {
        return callback(...args);
      }
      return callback;
    } finally {
      this.currentStore = previousStore;
      if (previousStore !== null) {
        globalAsyncStorage.set(this.id, previousStore);
        defaultStore = previousStore;
      } else {
        defaultStore = null;
      }
    }
  }
}

// Create a global instance that can be used by any code
const globalAsyncLocalStorage = new AsyncLocalStoragePolyfill();

// Aggressive global polyfill - override any existing implementations
const polyfillAsyncLocalStorage = () => {
  // Make AsyncLocalStorage available globally
  if (typeof globalThis !== 'undefined') {
    globalThis.AsyncLocalStorage = AsyncLocalStoragePolyfill;
    // Also create a default instance for direct usage
    if (!globalThis.__asyncLocalStorage) {
      globalThis.__asyncLocalStorage = globalAsyncLocalStorage;
    }
  }

  // Also make it available on window for browser compatibility
  if (typeof window !== 'undefined') {
    window.AsyncLocalStorage = AsyncLocalStoragePolyfill;
    if (!window.__asyncLocalStorage) {
      window.__asyncLocalStorage = globalAsyncLocalStorage;
    }
  }

  // Override any existing async_hooks module references
  if (typeof global !== 'undefined') {
    global.AsyncLocalStorage = AsyncLocalStoragePolyfill;
  }
};

// Apply polyfill immediately
polyfillAsyncLocalStorage();

// Also apply after a short delay to catch any late-loading modules
setTimeout(polyfillAsyncLocalStorage, 100);

export { AsyncLocalStoragePolyfill as AsyncLocalStorage };
export default AsyncLocalStoragePolyfill;